import type { DOMPurify } from 'dompurify'

export const SVG_MIME = 'image/svg+xml'

const SVG_NAMESPACE = 'http://www.w3.org/2000/svg'

/** Offers `.svg` by extension too, so the picker and drop zone keep it on
 * systems that map no MIME type to it. */
export const SVG_ACCEPT = `${SVG_MIME},.svg`

export const MAX_SVG_MB = 2

/** The browser's own type decides; the name is the fallback for systems that do
 * not map `.svg` to a MIME type, such as Windows without it registered. */
export const isSvgFile = (file?: { type?: string; name?: string } | null) =>
  file?.type ? file.type === SVG_MIME : /\.svg$/i.test(file?.name ?? '')

export type SvgRejection = 'tooLarge' | 'unreadable'

const BYTE_ORDER_MARKS: [number[], string][] = [
  [[0xef, 0xbb, 0xbf], 'utf-8'],
  [[0xff, 0xfe], 'utf-16le'],
  [[0xfe, 0xff], 'utf-16be']
]

/**
 * The file's text as the browser reads it: by its byte order mark, else by the
 * encoding its XML declaration names, else as UTF-8. `File.text()` always uses
 * UTF-8 and would garble a windows-1251 logo's lettering. Throws on an encoding
 * the browser does not know.
 */
export const decodeSvgText = (bytes: Uint8Array): string => {
  const bom = BYTE_ORDER_MARKS.find(([mark]) =>
    mark.every((byte, index) => bytes[index] === byte)
  )?.[1]
  const declared = /^<\?xml[^>]*\sencoding\s*=\s*["']([\w.:-]+)["']/.exec(
    new TextDecoder('windows-1252').decode(bytes.subarray(0, 256))
  )?.[1]

  return new TextDecoder(bom ?? declared ?? 'utf-8').decode(bytes)
}

let purifier: Promise<DOMPurify> | undefined

/** A private instance, loaded on first use: the shared default one also runs
 * the hooks other libraries add to it, mermaid's among them. */
const getPurifier = () =>
  (purifier ??= import('dompurify').then(({ default: createPurifier }) =>
    createPurifier(window)
  ))

const sanitizeSvg = async (file: File): Promise<File | SvgRejection> => {
  if (file.size > MAX_SVG_MB * 1024 * 1024) return 'tooLarge'

  let text: string
  try {
    text = decodeSvgText(new Uint8Array(await file.arrayBuffer()))
  } catch {
    return 'unreadable'
  }

  const parsed = new DOMParser().parseFromString(text, SVG_MIME)
  const root = parsed.documentElement
  if (
    parsed.getElementsByTagName('parsererror').length ||
    root.namespaceURI !== SVG_NAMESPACE ||
    root.localName !== 'svg'
  ) {
    return 'unreadable'
  }

  // RETURN_DOM hands back the <body> holding the result, typed only as a Node.
  const body = (await getPurifier()).sanitize(root, {
    USE_PROFILES: { svg: true, svgFilters: true },
    RETURN_DOM: true
  })
  const clean = body instanceof Element ? body.firstElementChild : null
  if (clean?.localName !== 'svg') return 'unreadable'

  // XMLSerializer, not innerHTML: it declares the SVG namespace, without which
  // the stored file is not an image at all.
  const markup = new XMLSerializer().serializeToString(clean)

  return new File([markup], file.name, { type: SVG_MIME })
}

const storable = new WeakMap<File, Promise<File | SvgRejection>>()

/**
 * `file` as it will be stored, or why it cannot be. A raster is stored as
 * picked; an SVG becomes its sanitized copy: parsed as XML, stripped by
 * DOMPurify of everything that is not drawing, written back as UTF-8. The copy
 * is remembered per file, so the preview shows exactly what `uploadFiles` stores.
 */
export const storableFile = (file: File): Promise<File | SvgRejection> => {
  if (!isSvgFile(file)) return Promise.resolve(file)

  let result = storable.get(file)
  if (!result) {
    result = sanitizeSvg(file)
    storable.set(file, result)
  }

  return result
}

/** Why `file` cannot be stored, or `null` when it can. Its message is
 * `svgUpload.<reason>`, taking `size: MAX_SVG_MB`. */
export const svgRejection = async (
  file: File
): Promise<SvgRejection | null> => {
  const result = await storableFile(file)

  return typeof result === 'string' ? result : null
}

/** An object URL showing `file` as it will be stored. Accepts a bare `Blob`, as
 * AntD's `previewFile` may pass one; it has no name and is shown as is. */
export const storedPreviewUrl = async (file: File | Blob) => {
  const result = file instanceof File ? await storableFile(file) : file

  return URL.createObjectURL(typeof result === 'string' ? file : result)
}
